import React from 'react';

import { openContextModal } from '@mantine/modals';
import { IncomingMessage } from 'http';
import { mutate } from 'swr';
import { snapshot } from 'valtio';

import { publicConfig } from '@public-config';

import {
    CreditUsageType,
    Invoice,
    MergeTeamsResult,
    SubscriptionBillingCycle,
    SubscriptionData,
    SubscriptionType,
    Team,
} from 'models';
import { TeamHelpers } from 'helpers/TeamHelpers';

import { InternalTrackingService } from 'services/InternalTrackingService';
import { state as currentTeamState } from 'state/current-team';
import { state as currentTeamsState } from 'state/current-teams';

import { ApiService } from './ApiService';
import { PayloadSDK } from './PayloadSDK';

const TeamService = {
    list: async () => {
        const { docs } = await PayloadSDK.find({
            collection: 'teams',
            select: {
                name: true,
            },
            pagination: false,
            sort: 'name',
        });

        return docs;
    },

    getByIds: async (teamIds: string[]): Promise<Team[]> => {
        if (!teamIds.length) {
            return [];
        }

        const { docs } = await PayloadSDK.find({
            collection: 'teams',
            where: {
                id: {
                    in: teamIds,
                },
            },
            depth: 1,
            limit: teamIds.length,
            sort: 'name',
        });

        return docs as unknown as Team[];
    },

    getAvailableTeams: async (req?: IncomingMessage) => {
        return ApiService.get<any[]>(`${publicConfig.urls.api}/teams/available-teams`, { req });
    },

    refreshAvailableTeams: async () => {
        const teams = await TeamService.getAvailableTeams();

        if (Array.isArray(teams)) {
            currentTeamsState.teams = teams;
        }

        return teams;
    },

    create: async (name: string): Promise<any> => {
        InternalTrackingService.track('team.create', {
            name,
        });

        const result = await ApiService.post(`${publicConfig.urls.api}/teams`, { name });

        return result;
    },

    get: async (teamId: string, req?: IncomingMessage): Promise<Team> => {
        if (currentTeamState.team?.id === teamId) {
            return snapshot(currentTeamState.team) as Team;
        }

        return ApiService.get(`${publicConfig.urls.api}/teams/${teamId}?depth=1`, { req });
    },

    getName: async (teamId: string): Promise<string> => {
        if (currentTeamState.team?.id === teamId) {
            return currentTeamState.team.name;
        }

        const team = await PayloadSDK.findByID({
            collection: 'teams',
            id: teamId,
            select: {
                name: true,
            },
            depth: 0,
        });

        return team?.name ?? 'Unknown Team';
    },

    getMultiple: async (teamIds: string[]) => {
        if (teamIds.length === 0) {
            return {
                docs: [],
                teams: [],
            };
        }

        const response = await ApiService.get<{
            docs: Team[];
        }>(`${publicConfig.urls.api}/teams?${teamIds.map((id) => `where[id][in]=${id}`).join('&')}`);

        return {
            ...response,
            teams: response.docs,
        };
    },

    refresh: async (teamId: string): Promise<Team> => {
        const updatedTeam = await ApiService.get<Team>(`${publicConfig.urls.api}/teams/${teamId}?depth=1`, {
            throwError: true,
        });

        mutate(`/api/teams/${teamId}`, updatedTeam, { revalidate: false });

        if (teamId === currentTeamState.team?.id) {
            currentTeamState.team = updatedTeam;
        }

        await TeamService.refreshAvailableTeams();

        return updatedTeam;
    },

    update: async (teamId: string, data: any): Promise<any> => {
        InternalTrackingService.track('team.update', {
            data,
        });

        const result = await ApiService.patch(`${publicConfig.urls.api}/teams/${teamId}`, data, { throwError: true });

        await TeamService.refresh(teamId);

        return result;
    },

    updateCurrentTeamName: (name: string) => {
        if (currentTeamState.team) {
            currentTeamState.team.name = name;
        }
    },

    subscriptionRefresh: async (teamId: string) => {
        return ApiService.post(`${publicConfig.urls.api}/teams/${teamId}/subscription-refresh`);
    },

    updateSubscription: async ({
        teamId,
        subscriptions,
        billingCycle,
    }: {
        teamId: string;
        subscriptions: SubscriptionData[];
        billingCycle: SubscriptionBillingCycle;
    }) => {
        return await ApiService.post(`${publicConfig.urls.api}/teams/${teamId}/update-subscription`, {
            subscriptions,
            billingCycle,
        });
    },

    subscriptionUnsubscribe: async (teamId: string, type: SubscriptionType) => {
        return await ApiService.post(`${publicConfig.urls.api}/teams/${teamId}/subscription-unsubscribe`, {
            type,
        });
    },

    subscriptionReleaseSchedule: async (teamId: string) => {
        return await ApiService.post(`${publicConfig.urls.api}/teams/${teamId}/subscription-release-schedule`);
    },

    getStripeInvoices: async (teamId: Team['id']): Promise<Invoice[]> => {
        return await ApiService.get(`${publicConfig.urls.api}/teams/${teamId}/invoices`);
    },

    getStripePortalUrl: (teamId: Team['id']): string => {
        return `${publicConfig.urls.api}/teams/${teamId}/stripe-portal`;
    },

    sendSubscriptionFeedback: async (feedback: string) => {
        return ApiService.post(`${publicConfig.urls.api}/teams/subscription-feedback`, {
            feedback,
        });
    },

    openCreateTeam: () => {
        openContextModal({
            modal: 'createTeam',
            innerProps: {},
            centered: true,
            withCloseButton: false,
        });
    },

    openSwitchTeamModal: ({
        create,
        switchy,
        force = false,
    }: {
        create: {
            title: string;
            description?: React.ReactNode;
        };
        switchy: {
            title: string;
            description?: React.ReactNode;
        };
        force?: boolean;
    }) => {
        const options = force
            ? {
                  closeOnClickOutside: false,
                  closeOnEscape: false,
              }
            : {};

        openContextModal({
            modal: 'genericSwitchTeam',
            innerProps: {
                force,
                create,
                switchy,
            },
            centered: true,
            withCloseButton: false,
            ...options,
        });
    },

    postCreateSubscription: (customerId: string) => {
        return ApiService.post(`${publicConfig.urls.api}/teams/post-subscribe/${customerId}`, {});
    },

    requestAccess: async (teamId: string) => {
        InternalTrackingService.track('team.requestAccess', {
            team: teamId,
        });

        return ApiService.post(`${publicConfig.urls.api}/teams/${teamId}/request-access`);
    },

    denyAccess: async (teamId: string, userId: string, message: string) => {
        InternalTrackingService.track('team.denyAccess', {
            teamId,
            userId,
            message,
        });

        return ApiService.post(`${publicConfig.urls.api}/teams/${teamId}/deny-access`, {
            userId,
            message,
        });
    },

    getCurrentTeamSubscription: () => {
        return TeamHelpers.getDesignerSubscription(currentTeamState.team);
    },

    getCurrentTeamCreditBalance: () => {
        return currentTeamState.team?.creditBalance ?? 0;
    },

    getCreditBalanceDetails: async (teamId: string) => {
        return await ApiService.get<CreditBalanceDetails>(
            `${publicConfig.urls.api}/teams/${teamId}/credit-balance-details`,
            { throwError: true },
        );
    },

    adminGetCreditOverview: async (teamId: string) => {
        return await ApiService.get<AdminCreditOverview>(
            `${publicConfig.urls.api}/teams/admin-credit-overview/${encodeURIComponent(teamId)}`,
            { throwError: true },
        );
    },

    createCreditCheckoutSession: async (teamId: string, quantity: number) => {
        const response = await ApiService.post<{ url: string }>(
            `${publicConfig.urls.api}/teams/${teamId}/credit-checkout-session`,
            { quantity },
        );

        return response.url;
    },

    join: async (id: string, code: string) => {
        await ApiService.post(`${publicConfig.urls.api}/teams/${id}/join?code=${code}`, {}, { throwError: true });
    },

    merge: async (originalId: string, contributingId: string, dryRun: boolean) =>
        ApiService.post<MergeTeamsResult>(
            `${publicConfig.urls.api}/teams/merge`,
            { originalId, contributingId, dryRun },
            { throwError: true },
        ),
};

type CreditBalanceDetails = {
    currentBalance: number;
    currentTotal: number;
    monthlyUsage: number;
    usage: Record<CreditUsageType, number>;
};

export type AdminCreditOverview = {
    team: Team;
    creditBalanceDetails: CreditBalanceDetails;
};

export { TeamService };
